/* eslint-disable max-len */
/* eslint-disable react/sort-prop-types */
/* eslint-disable array-callback-return */
/* eslint-disable react/no-unused-prop-types */
import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Callout,
  Dropdown,
  DropdownMenu,
  Button,
  Icon,
  NavListSection,
  NavListItem,
  NavList,
} from '@folio/stripes-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { stripesConnect } from '@folio/stripes-core';
import { isEqual } from 'lodash';
import ImportConfirmationModal from './ImportConfirmationModal';
import WithTranslations from './WithTranslations';
import CreateNewKeysModal from './CreateNewKeysModal';

const propTypes = {
  appName: PropTypes.string.isRequired,
  columnMapping: PropTypes.object,
  dataSource: PropTypes.string.isRequired,
  contentData: PropTypes.arrayOf(PropTypes.object).isRequired,
  fileName: PropTypes.string.isRequired,
  primaryField: PropTypes.string.isRequired,
  dataFormat: PropTypes.string.isRequired,
  onCreate: PropTypes.func,
  onUpdate: PropTypes.func,
  translatableFields: PropTypes.arrayOf(PropTypes.string),
  visibleFields: PropTypes.arrayOf(PropTypes.string).isRequired,
  updateTranslations: PropTypes.func.isRequired,
  bulkDeleteTranslationKeys: PropTypes.func.isRequired,
  stripes: PropTypes.object.isRequired,
};

const ActionsMenu = (props) => {
  const intl = useIntl();
  let fileReader;
  const inputRef      = useRef(null);
  const calloutRef    = useRef(null);

  const [openImportModal,   setOpenImportModal]   = useState(false);
  const [openSyncModal,     setOpenSyncModal]      = useState(false);
  const [dropdownOpen,      setDropdownOpen]       = useState(false);
  const [importedContent,   setImportedContent]    = useState([]);
  const [importedFileName,  setImportedFileName]   = useState('');

  // sync modal data — populated after POST /translation-keys/sync
  const [syncNewKeys,     setSyncNewKeys]     = useState({});
  const [syncOrphaned,    setSyncOrphaned]    = useState([]);
  const [syncLoading,     setSyncLoading]     = useState(false);

  const showCallout = (message, type = 'success') => {
    if (calloutRef.current) {
      calloutRef.current.sendCallout({ message, type });
    }
  };

  // ── Export ───────────────────────────────────────────────────────
  const getExportedContentData = (data) =>
    data.map((item) =>
      Object.keys(item)
        .filter((k) => !k.startsWith('translated'))
        .reduce((obj, k) => { obj[k] = item[k]; return obj; }, {})
    );

  const exportFile = (filename, data) => {
    const blob = new Blob([data], { type: 'application/json' });
    if (window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveBlob(blob, filename);
    } else {
      const elem = window.document.createElement('a');
      elem.href = window.URL.createObjectURL(blob);
      elem.download = filename;
      document.body.appendChild(elem);
      elem.click();
      document.body.removeChild(elem);
    }
  };

  const onHandleExport = () => {
    const fileName =
      typeof props.fileName === 'object'
        ? intl.formatMessage({ id: props.fileName.props.id })
        : props.fileName;
    exportFile(
      `${fileName}.json`,
      JSON.stringify(getExportedContentData(props.contentData), null, ' '),
    );
  };

  // ── Import ───────────────────────────────────────────────────────
  const filterContentData = (data) => {
    const arr = Array.isArray(data) ? data : [data];
    return arr.map((item) =>
      Object.keys(item)
        .filter((k) => !['metadata'].includes(k))
        .reduce((obj, k) => { obj[k] = item[k]; return obj; }, {})
    );
  };

  const handleUpdatedData = (contentData, updatedData) => {
    const arr = Array.isArray(contentData) ? contentData : [contentData];
    return updatedData.reduce((acc, upd) => {
      const match = arr.find(
        (item) => item[props.primaryField] === upd[props.primaryField]
      );
      if (match) { upd.id = match.id; acc.push(upd); }
      return acc;
    }, []);
  };

  const onReadFileContent = () => {
    setImportedContent(JSON.parse(fileReader.result));
    setOpenImportModal(true);
  };

  const onChooseImportedFile = (e) => {
    const file = e.target.files[0];
    fileReader = new FileReader();
    fileReader.onloadend = onReadFileContent;
    fileReader.readAsText(file);
    setImportedFileName(file.name);
  };

  const onSaveImportedFileContent = () => {
    const { contentData, onCreate, onUpdate } = props;
    const currentData  = filterContentData(contentData);
    const importedData = filterContentData(importedContent);

    const newData = importedData.filter(
      (imp) => !currentData.some(
        (cur) => imp[props.primaryField] === cur[props.primaryField]
      )
    );
    const updatedData = importedData.filter(
      (imp) => !currentData.some((cur) => isEqual(imp, cur))
    );

    const creates = newData.length
      ? Promise.all(newData.map((item) => onCreate(item)))
      : Promise.resolve();
    const updates = updatedData.length
      ? Promise.all(handleUpdatedData(contentData, updatedData).map(
          (item) => onUpdate(item)
        ))
      : Promise.resolve();

    Promise.all([creates, updates])
      .then(() => {
        setOpenImportModal(false);
        showCallout(
          intl.formatMessage({
            id: 'stripes-smart-components.actionMenu.import.success',
            defaultMessage: 'Import completed: {n} created, {u} updated',
          }, { n: newData.length, u: updatedData.length })
        );
      })
      .catch((e) => {
        setOpenImportModal(false);
        showCallout(
          intl.formatMessage({
            id: 'stripes-smart-components.actionMenu.import.error',
            defaultMessage: 'Import failed: {error}',
          }, { error: e.message }),
          'error'
        );
      });
  };

  // ── Sync (Create new keys) ────────────────────────────────────────
  const buildCurrentKeys = () => {
    const { translatableFields, appName, dataSource } = props;
    const entries = {};
    filterContentData(props.contentData).forEach((item) => {
      translatableFields?.forEach((field) => {
        if (item[field]) {
          entries[`${appName}.${dataSource}.${field}.${item[field]}`] = item[field];
        }
      });
    });
    return entries;
  };

  const onOpenSyncModal = async () => {
    const { appName, dataSource, translatableFields, stripes } = props;

    if (!translatableFields?.length) return;

    // بناء currentKeys قبل أي setState لتجنب تأثير re-render على props.contentData
    const currentKeys = buildCurrentKeys();

    setSyncLoading(true);
    setDropdownOpen(false);

    try {
      const res = await fetch(
        `${stripes.okapi.url}/translation-keys/sync`,
        {
          method: 'POST',
          headers: {
            'X-Okapi-Tenant': stripes.okapi.tenant,
            'X-Okapi-Token': stripes.okapi.token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            tableName: dataSource,
            appName,
            categoryName: 'libTranslations',
            currentKeys,
          }),
        }
      );
      const data = await res.json();

      // build newKeys map from currentKeys filtered to only truly new ones
      const createdCount = data.created || 0;
      const existingKeyNames = new Set(
        (data.orphaned || []).map((o) => o.translationKey)
      );
      // newKeys = currentKeys that are new (created > 0 means some were new)
      const newKeysForModal = {};
      Object.entries(currentKeys).forEach(([k, v]) => {
        if (!existingKeyNames.has(k)) newKeysForModal[k] = v;
      });

      setSyncNewKeys(data.newKeys || {});
      setSyncOrphaned(data.orphaned || []);
      setSyncLoading(false);
      setOpenSyncModal(true);
    } catch (e) {
      setSyncLoading(false);
      showCallout(
        intl.formatMessage({
          id: 'stripes-smart-components.actionMenu.sync.fetchError',
          defaultMessage: 'Failed to fetch sync data: {error}',
        }, { error: e.message }),
        'error'
      );
    }
  };

  // called by modal Step 1 — "Create only"
  const handleSyncCreateOnly = async () => {
    const entries = buildCurrentKeys();
    await props.updateTranslations('en', 'libTranslations', entries, {
      isSourceImport: true,
      conflictStrategy: 'skip',
    });
    return { created: Object.keys(syncNewKeys).length, deleted: 0 };
  };

  // called by modal Step 1 — "Create + Delete orphaned"
  const handleSyncCreateAndDelete = async () => {
    const entries = buildCurrentKeys();
    await props.updateTranslations('en', 'libTranslations', entries, {
      isSourceImport: true,
      conflictStrategy: 'skip',
    });
    const orphanKeys = syncOrphaned.map((o) => o.translationKey);
    const deleteRes = await props.bulkDeleteTranslationKeys({
      translationKeys: orphanKeys,
      categoryName: 'libTranslations',
    }).then((r) => r?.json?.() || {});

    return {
      created: Object.keys(syncNewKeys).length,
      deleted: deleteRes.deleted || orphanKeys.length,
    };
  };

  // ── Render helpers ────────────────────────────────────────────────
  const renderImportFileButton = () => (
    <input
      ref={inputRef}
      type="file"
      onChange={onChooseImportedFile}
      onClick={(e) => { e.target.value = null; }}
      id="importFiles"
      accept=".json"
      style={{ display: 'none' }}
    />
  );

  const renderImportConfirmationModal = () => (
    <ImportConfirmationModal
      onClose={() => setOpenImportModal(false)}
      onSave={onSaveImportedFileContent}
      open={openImportModal}
      contentData={filterContentData(importedContent)}
      visibleFields={props.visibleFields}
      dataFormat={props.dataFormat}
      fileName={importedFileName}
      columnMapping={props.columnMapping}
    />
  );

  const renderSyncModal = () => (
    <CreateNewKeysModal
      open={openSyncModal}
      onClose={() => setOpenSyncModal(false)}
      newKeys={syncNewKeys}
      orphanedKeys={syncOrphaned}
      fileName={props.fileName}
      onCreateOnly={handleSyncCreateOnly}
      onCreateAndDelete={handleSyncCreateAndDelete}
    />
  );

  const getDropdownContent = () => (
    <NavList>
      <NavListSection>
        <NavListItem
          id="clickable-import-file"
          type="button"
          onClick={() => inputRef.current.click()}
        >
          <Icon icon="import">
            <FormattedMessage
              id="stripes-smart-components.buttons.import"
              values={{ paneTitel: props.fileName }}
            />
          </Icon>
        </NavListItem>

        <NavListItem
          id="clickable-export-file"
          type="button"
          onClick={onHandleExport}
          disabled={!props.contentData.length}
        >
          <Icon icon="export">
            <FormattedMessage
              id="stripes-smart-components.buttons.export"
              values={{ paneTitel: props.fileName }}
            />
          </Icon>
        </NavListItem>

        <hr />

        <NavListItem
          id="clickable-create-new-translation-keys"
          type="button"
          onClick={onOpenSyncModal}
          disabled={syncLoading}
        >
          <Icon icon="plus-sign">
            <FormattedMessage
              id="stripes-smart-components.buttons.createTranslationKeys"
              defaultMessage="Create translation keys"
            />
          </Icon>
        </NavListItem>
      </NavListSection>
    </NavList>
  );

  const renderActionsMenuTrigger = ({ getTriggerProps }) => (
    <FormattedMessage id="stripes-core.mainnav.myProfileAriaLabel">
      {(label) => (
        <div style={{ paddingRight: '0.25em', paddingLeft: '0.25em' }}>
          <Button
            data-test-pane-header-actions-button
            buttonStyle="primary"
            marginBottom0
            ariaLabel={label}
            type="button"
            {...getTriggerProps()}
          >
            <Icon icon="ellipsis" size="large" />
          </Button>
        </div>
      )}
    </FormattedMessage>
  );

  const renderActionsMenu = ({ open }) => (
    <DropdownMenu open={open}>{getDropdownContent()}</DropdownMenu>
  );

  return (
    <>
      {renderImportFileButton()}
      {renderImportConfirmationModal()}
      {renderSyncModal()}
      <Dropdown
        id="editableList-actionsMenu-Dropdown"
        renderTrigger={renderActionsMenuTrigger}
        renderMenu={renderActionsMenu}
        open={dropdownOpen}
        onToggle={() => setDropdownOpen(!dropdownOpen)}
        placement="bottom-end"
        relativePosition
        focusHandlers={{ open: () => null }}
      />
      <Callout ref={calloutRef} />
    </>
  );
};

ActionsMenu.propTypes = propTypes;

export default stripesConnect(WithTranslations(ActionsMenu));
